
import { selectMe } from 'app/slice/user.slice'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import CardAppointment from 'component/Appointment/CardAppointment'
import SectionAppointment from 'component/Appointment/GraphAppointment'
import CollapseContainer from 'component/Collapse/container'
import SectionFilterDoctor from 'component/Home/filterDoctor'
import SendExamination from 'component/Register/SendExamination'
import SectionFilterPatient from 'features/home/filterPatient'
import SectionServicePoints from 'features/home/sliderServicePoints'

/** CONSTANTS */
import { colors } from 'constants/theme'

/** UTILS || SLICE */
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'

/** API */
import HomeApi from 'api/home/home.api'
import SendExaminationApi from 'api/master/sendExamination.api'
import BranchApi from 'api/setting/branch-service-points/branch.api'
import { routeName } from 'routes/routes-name'

/** STYLE */
import AboutUs from 'api/admin/clinic-manage/about.api'
import RegisterApi from 'api/counter/register.api'
import { swalCustom } from 'component/Alert/Swal'
import CardAppointmentDetail from 'component/Appointment/CardAppointmentDetail'
import { AppointmentCardItemDayInterface } from 'features/counter/appointment/appointment-interface'
import * as UseStyled from 'features/home/UseStyled'

const isRangeEndAfterTimeOut = (rangeText: string, timeOutText: string): boolean => {
  const [startRaw, endRaw] = rangeText.split(' - ').map(s => s.trim())
  const start = moment(startRaw.replace(/\./g, ':'), 'HH:mm')
  const end = moment(endRaw.replace(/\./g, ':'), 'HH:mm')
  if (!end.isAfter(start)) end.add(1, 'day')
  const timeOut = moment(timeOutText, 'HH:mm')
  if (timeOut.isBefore(start)) timeOut.add(1, 'day')
  return end.isAfter(timeOut)
}

export default function Home() {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const user = useSelector(selectMe)
  const [timeRange, setTimeRange] = useState<any>([])
  const [appointments, setAppointments] = useState<any>([])
  const [doctors, setDoctors] = useState<any>(null)
  const [temps, setTemps] = useState([])
  const [doctor, setDoctor]: any = useState({})
  const [hasCollapse, setHasCollapse] = useState(false)
  const [canViewIncome, setCanViewIncome] = useState(false)
  const [income, setIncome] = useState(0)
  const [servicePoints, setServicePoints] = useState([])
  const [sendExam, setSendExam] = useState(false)
  const [patientCase, setPatientCase] = useState<any | null>([])
  const [clinicIsClose, setClinicIsClose] = useState(false);
  const [viewDetails, setViewDetails] = useState<number[]>([])
  const branchId = getBranch()
  const [branch, setBranch] = useState<any>()
  const [key, setkey] = useState({ nonUsedKey: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const isMountedRef = useRef(true)
  const didMountRef = useRef(false)
  const [containerWidth, setContainerWidth] = useState<number>(394)

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    setContainerWidth(el.clientWidth || 394)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width
      if (w) setContainerWidth(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const dateHome = useMemo(() => {
    if (i18n.language === 'th') {
      return `${t('TIME_DURATION_TYPE.DAY')}${moment().format('dddd')}${t('DAY_AT')} `
    }
    return `${moment().format('dddd')} `
  }, [i18n.language, t])

  const doctorRef = useRef(doctor)
  useEffect(() => { doctorRef.current = doctor }, [doctor])

  const loadBranchInfo = useCallback(async () => {
    try {
      const { data } = await AboutUs.detailClinic(Number(branchId))
      if (isMountedRef.current) setBranch(data)
    } catch (_e) { /* ignore */ }
  }, [branchId])

  useEffect(() => {
    loadBranchInfo()
  }, [loadBranchInfo])

  const loadIncome = useCallback(async () => {
    if (!_.isEmpty(user) && user.hasViewIncome === 'PERMISS') {
      const respIncome = await HomeApi.clinicIncome()
      if (!isMountedRef.current) return
      if (respIncome.status === 200) {
        setIncome(respIncome.data || 0)
        setCanViewIncome(true)
      }
    } else {
      setIncome(0)
      setCanViewIncome(false)
    }
  }, [user])

  const loadListQueue = useCallback(async () => {
    const queue = await HomeApi.listQueue()
    if (!isMountedRef.current) return
    if (queue.status === 200 && !_.isEmpty(queue.data)) setServicePoints(queue.data)
    else setServicePoints([])
  }, [])

  const loadAllAppointment = useCallback(async () => {
    const respAppointments = await HomeApi.allAppointment()
    if (!isMountedRef.current) return
    if (respAppointments.status === 200 && !_.isEmpty(respAppointments.data)) {
      setAppointments(respAppointments.data)
    } else setAppointments([])
  }, [])

  const loadAllDentists = useCallback(async () => {
    const selectDoc = doctorRef.current
    const dentists = await HomeApi.allDentists()
    if (!isMountedRef.current) return
    setkey({ nonUsedKey: Date.now() })
    if (dentists.status === 200 && !_.isEmpty(dentists.data)) {
      setDoctors(dentists.data)
      setTemps(_.clone(dentists.data))
      if (_.isEmpty(selectDoc)) {
        setDoctor(dentists.data[0])
        return
      }
      const item = _.find(dentists.data, { doctorId: selectDoc.doctorId })
      if (item) setDoctor(item)
    } else {
      setDoctor({})
      setDoctors([])
      setTemps([])
    }
  }, [])

  const openVisitAppointment = async (id: number) => {
    const visit = await SendExaminationApi.homeOpenVisit(id)
    if (visit.status === 201 && !_.isEmpty(visit.data)) {
      setPatientCase(visit.data)
      setSendExam(true)
    } else setPatientCase([])
  }

  const onSendToRegister = (data: any) => {
    history.push({
      pathname: routeName.register,
      state: { newPatientInfo: data }
    })
  }

  const handleTimeBranch = async (branchId: number) => {
    if (branchId) {
      BranchApi.findById(branchId)
        .then(({ data }) => {
          const day = _.find(data.officeTimes, (time) => time.dayOfWeek === moment().locale('en').format('ddd'))
          if (day?.officeStatus === 'CLOSE') setClinicIsClose(true)
          let start: any = moment(day?.timeIn, 'HH:mm')
          let end: any = moment(day?.timeOut, 'HH:mm')
          if (parseInt(start.format('mm')) > 0) start = moment(data.timeIn, 'HH:mm').startOf('hour')
          if (parseInt(end.format('mm')) > 0) end = moment(data.timeOut, 'HH:mm').endOf('hour').add(1, 'minute')
          const timeEnd = parseInt(end.format('HH')) === 0 ? 24 : parseInt(end.format('HH'))
          const range: any = []
          for (let index = parseInt(start.format('HH')); index <= timeEnd; index++) {
            range.push(`${String(index).padStart(2, '0')}:00`)
          }
          if (!_.isEmpty(range)) setTimeRange(range)
          else setTimeRange(['9.00', '10.00', '11.00', '12.00', '13.00', '14.00', '15.00', '16.00', '17.00', '18.00'])
        })
    }
  }

  const handleSelectAppoinment = useCallback((val: any) => {
    if (val.appointmentId) {
      const dr: any = _.find(temps, { doctorId: val.doctorId })
      const apt = _.filter(dr?.appointments, { appointmentId: Number(val.appointmentId) })
      setDoctor({ ...(dr || {}), appointments: apt })
    } else {
      setDoctor(temps[0])
    }
  }, [temps])

  const handleSelectDoctor = useCallback((event: any) => {
    const id = event.target.value || event.target.offsetParent.value || event.target.alt
    const item = _.find(temps, { doctorId: Number(id) })
    if (id && item) setDoctor(item)
  }, [temps])

  const handleCollapse = useCallback((status: boolean) => {
    setHasCollapse(status)
  }, [])

  const onFilterPatientChange = useCallback((event: any, value: any) => {
    if (value) handleSelectAppoinment(value)
    else handleSelectAppoinment('')
  }, [handleSelectAppoinment])

  const handleCallback = useCallback((value: any) => {
    if (value.key === 'openVisit') {
      setPatientCase(value.value)
      setSendExam(true)
    } else if (value.key === 'reloadListQueue' && value.value === 'success') {
      loadListQueue()
    }
  }, [loadListQueue])

  const reRenderAll = useCallback(() => {
    loadListQueue()
    loadAllAppointment()
    loadAllDentists()
  }, [loadListQueue, loadAllAppointment, loadAllDentists])

  const dataAlert = useSelector((state: any) => state.modal.dataAlert)
  useEffect(() => {
    loadListQueue()
    loadAllAppointment()
    loadAllDentists()
  }, [dataAlert, loadListQueue, loadAllAppointment, loadAllDentists])

  useEffect(() => {
    loadIncome()
    handleTimeBranch(user?.roles?.roleBranch?.branchId || 0)
  }, [user])

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    if (!sendExam) {
      loadListQueue()
      loadAllDentists()
    }
  }, [sendExam, loadListQueue, loadAllDentists])

  const handleViewDetail = useCallback((val: any) => {
    setViewDetails((prev: any) => (prev.includes(val) ? prev : [...prev, val]))
  }, [])

  const handleCloseDetail = useCallback((val: any) => {
    setViewDetails((prev: any) => prev.filter((t: any) => t !== val))
  }, [])

  const viewDetailsSet = useMemo(() => new Set(viewDetails), [viewDetails])

  const groupedAppointments = useMemo(() => {
    const apts = Array.isArray(doctor.appointments) ? doctor.appointments : []
    const sorted = [...apts].sort((a: any, b: any) => {
      const aTime = moment(a.start, 'HH:mm')
      const bTime = moment(b.start, 'HH:mm')
      if (!aTime.isValid() || !bTime.isValid()) return 0
      return aTime.valueOf() - bTime.valueOf()
    })
    const grouped = _.groupBy(sorted, 'start')
    return _.map(grouped, (value: any) => {
      const doctorCount = _.countBy(value, 'doctorId')
      return { data: value, doctorCount }
    })
  }, [doctor.appointments])

  const getPatientCode = useCallback((i: any) => {
    if (branch?.patientCodeAppointment === '1') return i.cnNumber
    if (branch?.patientCodeAppointment === '0') return i.branchCnNumber
    if (branch?.patientCodeAppointment === '2') return i.snCode
    return ''
  }, [branch?.patientCodeAppointment])

  const handleCardViewDetail = useCallback(async (i: any) => {
    const resp = await RegisterApi.findPatientFinanceByPatientId(i?.patientId)
    if (branch?.openNotification === '1' && resp.data?.outstandingBalance > 0) {
      return swalCustom(
        `${t('REGISTER.ALERT.NOTIFICATION_OVERDUE')}`,
        `<div>
                <div className="pt-2" style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <div style={{ margin: 0, padding: 0 }}>${t('REGISTER.ALERT.PLEASE_PAY_OVERDUE_AMOUNT')}</div>
                  <p style={{ margin: 0, padding: 0 }}>${t('REGISTER.ALERT.PRESS')} <strong>"${t('REGISTER.ALERT.CONFIRM')}"</strong>${t('REGISTER.ALERT.TOTAL_OVERDUE_AMOUNT')}</p>
                </div>
                <div className="pt-2" style={{ textAlign: 'left', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '2px'}}>
                  <span class="text-left d-flex align-items-start"><strong>${t('REGISTER.ALERT.NOTE')}: </strong></span>
                  <span class="text-left d-flex align-items-start"><strong>${t('REGISTER.ALERT.TOTAL_OVERDUE_AMOUNT')} </strong> &nbsp;${resp.data?.outstandingBalance}&nbsp; <strong>${t('REGISTER.ALERT.BAHT')}</strong></span>
                </div>
              </div>`,
        (res: any) => { if (res) history.push(routeName.overdueDetail, { patientId: i?.patientId }) },
        'warning',
        {
          confirmButton: { confirmButtonText: t('BUTTON.SUBMIT') },
          cancelButton: { cancelButtonText: t('BUTTON.CANCEL') }
        }
      )
    }
    if (!i.cnNumber || !i.branchCnNumber) onSendToRegister(i)
    else if (i.statusAppointment === "DONE") handleViewDetail(i.appointmentId)
    else openVisitAppointment(i.appointmentId)
  }, [branch?.openNotification, t, history, handleViewDetail])

  const cards = useMemo(() => {
    if (_.isEmpty(timeRange)) return []
    return _.map(groupedAppointments, (time: any, timeIndex: number) =>
      _.map(time.data, (i: any, indexAppointments: number) => {
        const allRow = time.doctorCount[i.doctorId] || 0
        const serviceText = _.map(i.appointmentOperatives, (operative: any) =>
          `${operative.operativeId ? operative.operativeName : operative.operativeTypeName} `
        ).join('')
        return (
          <div key={`apt-${i.appointmentId}-${timeIndex}-${indexAppointments}`}>
            <CardAppointment
              appointmentId={i.appointmentId}
              key={`card-${i.appointmentId}`}
              serviceText={serviceText}
              rangeTimeText={i.rangeTimeText}
              colorBackground={i.appointmentOperatives.length === 1 ? i.appointmentOperatives[0].color : colors.white}
              status={i.statusAppointment}
              branchCnNumber={getPatientCode(i)}
              patientFullName={i.patientFullname}
              start={i.start || i.rangeTimeText.split(' - ')[0].replace('.', ':')}
              end={
                isRangeEndAfterTimeOut(i?.rangeTimeText, branch?.timeOut) ? moment(branch?.timeOut, 'HH:mm').add(20, 'minutes').format('HH:mm') : i.end || i.rangeTimeText.split(' - ')[1].replace('.', ':')
              }
              hasLab={i.hasLab === 'HAS'}
              hasXray={i.hasXray === 'HAS'}
              timeStart={timeRange[0]}
              onViewDetail={() => handleCardViewDetail(i)}
              specialInfo={i?.specialInfo}
              width={(containerWidth / allRow)}
              translateX={(containerWidth / allRow) * indexAppointments}
              phone={i?.phone}
              allRow={allRow >= 4}
            />
          </div>
        )
      })
    )
  }, [groupedAppointments, timeRange, branch?.timeOut, containerWidth, getPatientCode, handleCardViewDetail])

  const popups = useMemo(() => {
    return _.map(doctor.appointments, (ap: AppointmentCardItemDayInterface, j: number) => (
      <CardAppointmentDetail
        key={j + `key${key.nonUsedKey}`}
        timeStart={timeRange[0]}
        timeEnd={timeRange[timeRange.length - 1]}
        appointmentId={ap.appointmentId}
        showPopup={viewDetailsSet.has(ap.appointmentId)}
        onClose={handleCloseDetail}
        onEdit={undefined}
        hideMenu={true}
        onEditSpecialInfo={undefined}
        status={ap.statusAppointment}
        branch={branch} />
    ))
  }, [doctor.appointments, timeRange, viewDetailsSet, handleCloseDetail, branch, key.nonUsedKey])

  return (
    <div className={'h-100 overflow-hidden'}>
      <CollapseContainer
        key={key.nonUsedKey}
        tooltipExpand={t('HOME.TOOLTIP.EXPAND_SCHEDULE')}
        tooltipShorten={t('HOME.TOOLTIP.SHORTEN_SCHEDULE')}
        styleComponentLeft={{ height: '100%' }}
        styleComponentRight={{ height: '100%' }}
        callBackStatusCollapse={handleCollapse}
        componentLeft={
          <div className={'schedule h-100 py-4 d-flex flex-column'}>
            <UseStyled.Container>
              <UseStyled.TypoHeader className={'text-ellipsis'}>
                <span className={'title-schedule pr-1'}>{t('HOME.TITLE.SCHEDULE')}</span>
                {`${dateHome}`}{toBuddhistYear(moment(), `D MMMM YYYY`)}
              </UseStyled.TypoHeader>
              <div className={'mb-3 mb-xl-4'}>
                <SectionFilterPatient
                  data={appointments}
                  onchange={onFilterPatientChange}
                  disabled={clinicIsClose}
                  branch={branch}
                />
              </div>
              <div className={'ml-4 mr-2'}>
                <SectionFilterDoctor doctors={doctors} key={key.nonUsedKey ?? 1} filterDoctor={doctor} onClick={handleSelectDoctor} clinicIsClose={clinicIsClose}></SectionFilterDoctor>
              </div>
            </UseStyled.Container>

            <div className={'h-100 overflow-hidden d-flex flex-row'}>
              <div className={'graph-appointment'} style={{ width: 'calc(100% - 2rem)' }} ref={containerRef}>
                <SectionAppointment
                  timeRange={timeRange}
                  width={"100%"}
                  card={cards}
                  popup={popups}
                  showMinute
                />
              </div>
            </div>
          </div>
        }
        componentRight={
          <div className={'service-points h-100 pb-4 d-flex flex-column'} >
            <UseStyled.Container>
              <UseStyled.Header className={'d-flex align-items-center'}>
                <UseStyled.TypoHeader className={'text-ellipsis mb-0'}>{t('HOME.TITLE.SERVICE_POINTS')}</UseStyled.TypoHeader>
                {canViewIncome && (
                  <UseStyled.Income>
                    <span className={'pr-1'}>{t('HOME.TITLE.INCOME')}</span>
                    <span className={'ml-auto'}>{numberFormat(income)}</span>
                    <span className={'pl-1'}>{t('HOME.TITLE.CURRENCY')}</span>
                  </UseStyled.Income>
                )}
              </UseStyled.Header>
            </UseStyled.Container>
            {/* overflow-hidden */}
            <UseStyled.Container className={'pr-0 h-100 '} style={{ position: 'relative' }}>
              <SectionServicePoints
                branch={branch}
                service={servicePoints}
                hasCollapse={hasCollapse}
                setCancelCase={handleCallback}
                setPatientCase={handleCallback}
                reRender={reRenderAll}
              />
            </UseStyled.Container>
          </div >
        }
      ></CollapseContainer >
      {sendExam && <SendExamination isShow={sendExam} setIsShow={(show: boolean) => setSendExam(show)} patient={patientCase} type={'home'} service={servicePoints} />}

      {/* <div style={{ display: "none" }}>
        <ModalCustomNotification />
      </div> */}

    </div >
  )
}
